import { useState, useEffect } from 'react'

interface PricingInfo {
  price: number
  regularPrice: number
  symbol: string
  locale: string
  currencyCode: string
  formattedPrice: string
  formattedRegularPrice: string
}

interface RegionState extends PricingInfo {
  loading: boolean
  error: boolean
}

const FALLBACK_PRICING = {
  price: 37,
  regularPrice: 67,
  symbol: '$',
  locale: 'en-US',
  currencyCode: 'USD',
}

const PRICING_MAP: Record<
  string,
  Omit<PricingInfo, 'formattedPrice' | 'formattedRegularPrice'>
> = {
  // US, CA, AU, UK, NZ
  US: {
    price: 37,
    regularPrice: 67,
    symbol: '$',
    locale: 'en-US',
    currencyCode: 'USD',
  },
  CA: {
    price: 37,
    regularPrice: 67,
    symbol: '$',
    locale: 'en-US',
    currencyCode: 'USD',
  },
  AU: {
    price: 37,
    regularPrice: 67,
    symbol: '$',
    locale: 'en-US',
    currencyCode: 'USD',
  },
  GB: {
    price: 37,
    regularPrice: 67,
    symbol: '$',
    locale: 'en-US',
    currencyCode: 'USD',
  },
  NZ: {
    price: 37,
    regularPrice: 67,
    symbol: '$',
    locale: 'en-US',
    currencyCode: 'USD',
  },

  // Europe
  DE: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },
  FR: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },
  ES: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },
  IT: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },
  NL: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },
  BE: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },
  PT: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },
  AT: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },
  IE: {
    price: 34,
    regularPrice: 62,
    symbol: '€',
    locale: 'de-DE',
    currencyCode: 'EUR',
  },

  // LATAM & Others
  MX: {
    price: 680,
    regularPrice: 1230,
    symbol: '$',
    locale: 'es-MX',
    currencyCode: 'MXN',
  },
  BR: {
    price: 185,
    regularPrice: 335,
    symbol: 'R$',
    locale: 'pt-BR',
    currencyCode: 'BRL',
  },
  AR: {
    price: 35000,
    regularPrice: 63500,
    symbol: '$',
    locale: 'es-AR',
    currencyCode: 'ARS',
  },
  CO: {
    price: 155000,
    regularPrice: 281000,
    symbol: '$',
    locale: 'es-CO',
    currencyCode: 'COP',
  },
}

const formatPrice = (amount: number, locale: string, currency: string) => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch (e) {
    // Fallback if Intl fails
    return `${amount} ${currency}`
  }
}

export function useUserRegion(): RegionState {
  const [state, setState] = useState<RegionState>({
    ...FALLBACK_PRICING,
    formattedPrice: formatPrice(
      FALLBACK_PRICING.price,
      FALLBACK_PRICING.locale,
      FALLBACK_PRICING.currencyCode,
    ),
    formattedRegularPrice: formatPrice(
      FALLBACK_PRICING.regularPrice,
      FALLBACK_PRICING.locale,
      FALLBACK_PRICING.currencyCode,
    ),
    loading: true,
    error: false,
  })

  useEffect(() => {
    let mounted = true

    const fetchRegion = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/')
        if (!response.ok) throw new Error('Failed to fetch region')

        const data = await response.json()
        const countryCode = data.country_code

        if (mounted) {
          const pricing = PRICING_MAP[countryCode] || FALLBACK_PRICING

          setState({
            ...pricing,
            formattedPrice: formatPrice(
              pricing.price,
              pricing.locale,
              pricing.currencyCode,
            ),
            formattedRegularPrice: formatPrice(
              pricing.regularPrice,
              pricing.locale,
              pricing.currencyCode,
            ),
            loading: false,
            error: false,
          })
        }
      } catch (error) {
        console.error('Error detecting region:', error)
        if (mounted) {
          setState((prev) => ({ ...prev, loading: false, error: true }))
        }
      }
    }

    fetchRegion()

    return () => {
      mounted = false
    }
  }, [])

  return state
}
